import { QueryFieldFilterConstraint, where } from 'firebase/firestore'
import { cloneDeep } from 'lodash'

import FirestoreService from '../services/firestoreService'

import { getConvertedData } from '../models/interface.helper'

import { IRegistrationFeesInterface } from '../models/registration-fees/registrationFees.interface'
import { IRegistrationByDayInterface } from '../models/registrations-by-day/registrationByDay.interface'
import { IRegistrationTicketInterface } from '../models/registration-tickets/registrationTicket.interface'
import { IEventReviewPublish } from '../models/event-review-publish/event-review-publish.interface'
import { RegistrationFeesType } from '../models/event-fees/event-fees.interface'
import { IRegistrationTabs } from '../models/event-registered-users/event-registered-users.interface'

import { RegistrationByDayModel } from '../models/registrations-by-day/registrationByDay.model'
import { EventReviewPublishModel } from '../models/event-review-publish/event-review-publish.model'
import { RegistrationTicketModel } from '../models/registration-tickets/registrationTicket.model'
import { RegistrationFeesModel } from '../models/registration-fees/registrationFees.model'

import { CONST } from '../const/const'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

export const getRegistrationData = async (
  userId: string,
  registrationId: string,
  eventId: string
) => {
  let registrationsFees: IRegistrationFeesInterface[] = []
  let registrationsByDay: IRegistrationByDayInterface[] = []
  let registrationsTickets: IRegistrationTicketInterface[] = []
  let publishedEventInDb: IEventReviewPublish | null = null

  let commonQueries = [
    where(COLLECTIONS.REGISTRATION_BY_DAY.FIELDS.EVENT_ID.KEY, '==', eventId),
    where(COLLECTIONS.REGISTRATION_BY_DAY.FIELDS.REGISTRATION_DOC_ID.KEY, '==', registrationId),
  ]

  commonQueries.push(where(COLLECTIONS.REGISTRATION_BY_DAY.FIELDS.USER_ID.KEY, '==', userId))

  let queryConstraint = {
    Fees: {
      query: [] as QueryFieldFilterConstraint[],
      collection: COLLECTIONS.REGISTRATION_FEES.NAME,
    },
    Tickets: {
      query: [] as QueryFieldFilterConstraint[],
      collection: COLLECTIONS.REGISTRATION_TICKET.NAME,
    },
    Classes: {
      query: [] as QueryFieldFilterConstraint[],
      collection: COLLECTIONS.REGISTRATION_BY_DAY.NAME,
    },
  }

  queryConstraint.Classes.query = commonQueries
  queryConstraint.Fees.query = commonQueries
  queryConstraint.Tickets.query = commonQueries

  if (queryConstraint.Classes.query.length) {
    const commonSnaps = await FirestoreService.filterItems(
      queryConstraint.Classes.collection,
      queryConstraint.Classes.query
    )

    commonSnaps.docs.forEach((currDoc) => {
      const current = getConvertedData(RegistrationByDayModel.fromFirestoreDoc(currDoc).toObject())
      registrationsByDay.push(current)
    })
  }

  const publishedEventInDbSnapShot = await FirestoreService.getItem(
    COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME,
    eventId
  )

  if (publishedEventInDbSnapShot.exists()) {
    publishedEventInDb = getConvertedData(
      EventReviewPublishModel.fromFirestoreDoc(publishedEventInDbSnapShot).toObject()
    )

    const { EventFees: { registrationFees } = {} } = publishedEventInDb
    registrationsByDay = registrationsByDay.map((registrationByDay) => {
      const filtered = (registrationFees as RegistrationFeesType[]).filter(
        (fee) => fee.uuid === registrationByDay.uuid
      )
      if (filtered.length > 0) {
        return { ...registrationByDay, registrationByDayName: filtered[0].name ?? null }
      } else {
        return registrationByDay
      }
    })
  }

  if (queryConstraint.Tickets.query.length) {
    const commonSnaps = await FirestoreService.filterItems(
      queryConstraint.Tickets.collection,
      queryConstraint.Tickets.query
    )

    commonSnaps.docs.forEach((currDoc) => {
      registrationsTickets.push(
        getConvertedData(RegistrationTicketModel.fromFirestoreDoc(currDoc).toObject())
      )
    })
  }

  if (queryConstraint.Fees.query.length) {
    const commonSnaps = await FirestoreService.filterItems(
      queryConstraint.Fees.collection,
      queryConstraint.Fees.query
    )

    commonSnaps.docs.forEach((currDoc) => {
      const registrationFee = getConvertedData(
        RegistrationFeesModel.fromFirestoreDoc(currDoc).toObject()
      )
      if (registrationFee.selectedUnitsCount > 0) registrationsFees.push(registrationFee)
    })
  }

  return { registrationsByDay, registrationsFees, registrationsTickets, publishedEventInDb }
}

export function mergeRegistrationWithTabData(
  registerTabData: IRegistrationTabs['IRegisterTab'][],
  registrationsByDay: IRegistrationByDayInterface[]
) {
  let registerTabDataCloned = cloneDeep(registerTabData)
  let registrationsByDayCloned = cloneDeep(registrationsByDay)
  registerTabDataCloned = registerTabDataCloned.map((currRow) => {
    return {
      ...currRow,
      registrationsByDay: [],
    }
  })
  return registerTabDataCloned.map((item) => {
    const registrationsByDay = registrationsByDayCloned.filter(
      (obj) => obj.uuid === item.uuid && obj.registrationByDayName === item.registrationByDayName
    )

    const newChildren = cloneDeep(item?.children)?.map((child) => {
      const registrationsByDayChildren = registrationsByDayCloned.filter(
        (obj) => obj.uuid === child.uuid
      )
      return { ...child, registrationsByDay: registrationsByDayChildren }
    })

    return { ...item, registrationsByDay, children: newChildren }
  })
}

export const getRegistrationsByDayUpdated = ({
  registrationsByDayToDelete,
  registrationsByDayToAdd,
  registrationsByDayToUpdate,
  registrationsByDay,
}: {
  registrationsByDayToDelete?: IRegistrationByDayInterface[] | null
  registrationsByDayToAdd?: IRegistrationByDayInterface[] | null
  registrationsByDayToUpdate?: IRegistrationByDayInterface[] | null
  registrationsByDay: IRegistrationByDayInterface[]
}) => {
  let registrationsByDayUpdated: IRegistrationByDayInterface[]

  if (registrationsByDayToDelete?.[0]) {
    const idsToDelete = registrationsByDayToDelete.map((item) => item.uniqId)

    registrationsByDayUpdated = registrationsByDay.filter(
      (registration) => !idsToDelete.includes(registration.uniqId)
    )
  } else if (registrationsByDayToUpdate?.[0]) {
    const idsToUpdate = registrationsByDayToUpdate.map((item) => item.uniqId)

    registrationsByDayUpdated = cloneDeep(registrationsByDay)?.map((registrationByDay) => {
      const isUpdate = idsToUpdate.includes(registrationByDay?.uniqId)

      const filtered = registrationsByDayToUpdate.filter(
        (registrationByDayToUpdate) => registrationByDayToUpdate.uniqId === registrationByDay.uniqId
      )

      if (isUpdate) {
        return filtered[0]
      } else {
        return registrationByDay
      }
    })
  } else {
    registrationsByDayUpdated = getConvertedData([
      ...registrationsByDay,
      ...(registrationsByDayToAdd ?? []),
    ])
  }

  return registrationsByDayUpdated
}
